import React, {
  createContext, useContext, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { getRowFieldLabel, toSnakeCase } from './review-embedded-field-labels';

/**
 * Broker-verification field-edit metrics. Only the Review page provides this context (see
 * ReviewEditTrackingProvider.js), so the Financials/Lease Details components shared with the
 * live tabs (via `useFormState` etc.) are complete no-ops everywhere else. Kept free of
 * redux/service imports on purpose: live-tab modules import this file, so it must stay light.
 */
const ReviewEditTrackingContext = createContext(null);
const ReviewEditRowContext = createContext(null);

const NOOP = () => {};

/** Describes which embedded Financials row (consideration, commission, ...) is being edited. */
const ReviewEditRowScope = ({
  rowType, rowId, section, children,
}) => {
  const value = useMemo(() => ({ rowType, rowId, section }), [rowType, rowId, section]);
  return <ReviewEditRowContext.Provider value={value}>{children}</ReviewEditRowContext.Provider>;
};

ReviewEditRowScope.propTypes = {
  rowType: PropTypes.string.isRequired,
  rowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  section: PropTypes.string,
  children: PropTypes.node.isRequired,
};

ReviewEditRowScope.defaultProps = {
  rowId: undefined,
  section: undefined,
};

/** Returns `trackEdit(edit)` on the Review page, `null` anywhere else. */
const useReviewEditTracker = () => useContext(ReviewEditTrackingContext)?.trackEdit || null;
const useReviewEditRow = () => useContext(ReviewEditRowContext);

/**
 * For embedded Financials rows: returns `report(fieldName, previousValue, currentValue)`.
 * Outside the Review page (or with `skip`) it is one shared no-op, so live tabs pay no string
 * work and get a stable reference. A metrics failure can never break the caller's save.
 */
const useReviewRowEditReporter = ({
  rowType: explicitRowType, rowId: explicitRowId, skip = false,
} = {}) => {
  const trackEdit = useReviewEditTracker();
  const scope = useReviewEditRow();
  const rowType = explicitRowType || scope?.rowType;
  const rowId = explicitRowId ?? scope?.rowId;
  const section = scope?.section;

  return useMemo(() => {
    if (!trackEdit || skip || !rowType) return NOOP;
    const snakeRowType = toSnakeCase(rowType);
    return (fieldName, previousValue, currentValue) => {
      try {
        trackEdit({
          fieldKey: `${snakeRowType}.${fieldName}`,
          fieldLabel: getRowFieldLabel(snakeRowType, fieldName),
          section,
          seedPrevious: previousValue,
          currentValue,
          entityType: snakeRowType,
          entityId: rowId,
          rowType: snakeRowType,
        });
      } catch (error) {
        console.error('Failed to track broker verification field edit');
      }
    };
  }, [trackEdit, skip, rowType, rowId, section]);
};

export {
  ReviewEditTrackingContext, ReviewEditRowScope, useReviewEditTracker, useReviewEditRow, useReviewRowEditReporter,
};
